(() => {
  const SELECTOR = '[data-digi-sticky-first-buy]';

  class DigiStickyFirstBuy {
    constructor(element) {
      this.element = element;
      this.button = element.querySelector(
        '[data-digi-sticky-first-buy-button]'
      );

      this.targetSelector =
        element.dataset.targetSelector ||
        '.digi-first-purchase-hero';

      this.shouldFocusForm =
        element.dataset.focusForm === 'true';

      this.target = null;
      this.isVisible = false;
      this.rafId = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleClick = this.handleClick.bind(this);

      this.init();
    }

    init() {
      if (this.element.dataset.initialized === 'true') {
        return;
      }

      this.target = document.querySelector(this.targetSelector);

      if (!this.target || !this.button) {
        this.hide();
        return;
      }

      this.element.dataset.initialized = 'true';

      this.button.addEventListener('click', this.handleClick);

      window.addEventListener('scroll', this.handleScroll, {
        passive: true
      });

      window.addEventListener('resize', this.handleResize, {
        passive: true
      });

      this.updateVisibility();
    }

    handleScroll() {
      this.requestUpdate();
    }

    handleResize() {
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.updateVisibility();
        this.rafId = null;
      });
    }

    updateVisibility() {
      if (!this.target) {
        this.hide();
        return;
      }

      const targetRect = this.target.getBoundingClientRect();

      /*
       * O CTA aparece somente quando o usuário já passou
       * completamente pelo hero.
       *
       * Se o hero ainda estiver visível ou estiver abaixo
       * da tela, o botão continua oculto.
       */
      const hasPassedTarget = targetRect.bottom <= 0;

      if (hasPassedTarget) {
        this.show();
      } else {
        this.hide();
      }
    }

    show() {
      if (this.isVisible) {
        return;
      }

      this.isVisible = true;
      this.element.classList.add('is-visible');
      this.element.setAttribute('aria-hidden', 'false');
    }

    hide() {
      if (!this.isVisible) {
        return;
      }

      this.isVisible = false;
      this.element.classList.remove('is-visible');
      this.element.setAttribute('aria-hidden', 'true');
    }

    handleClick() {
      if (!this.target) {
        return;
      }

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.target.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });

      /*
       * O botão é ocultado imediatamente para não continuar
       * sobre a tela durante o retorno ao formulário.
       */
      this.hide();

      if (this.shouldFocusForm) {
        this.focusFirstField(prefersReducedMotion);
      }
    }

    focusFirstField(prefersReducedMotion) {
      const focusDelay = prefersReducedMotion ? 0 : 650;

      window.setTimeout(() => {
        const firstField = this.target.querySelector(
          [
            '.digi-first-purchase-form input:not([type="hidden"]):not([disabled])',
            '.digi-first-purchase-form select:not([disabled])',
            '.digi-first-purchase-form textarea:not([disabled])',
            '.digi-first-purchase-hero__connectif input:not([type="hidden"]):not([disabled])',
            '.digi-first-purchase-hero__connectif select:not([disabled])',
            '.digi-first-purchase-hero__connectif textarea:not([disabled])'
          ].join(',')
        );

        if (!firstField) {
          return;
        }

        /*
         * preventScroll evita um segundo salto quando o campo
         * recebe o foco depois da rolagem.
         */
        firstField.focus({
          preventScroll: true
        });
      }, focusDelay);
    }

    destroy() {
      this.button?.removeEventListener(
        'click',
        this.handleClick
      );

      window.removeEventListener(
        'scroll',
        this.handleScroll
      );

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
      }

      delete this.element.dataset.initialized;
    }
  }

  const instances = new Map();

  const initialize = (scope = document) => {
    const elements = [];

    if (scope.matches?.(SELECTOR)) {
      elements.push(scope);
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => elements.push(element));

    elements.forEach((element) => {
      if (instances.has(element)) {
        return;
      }

      instances.set(
        element,
        new DigiStickyFirstBuy(element)
      );
    });
  };

  const destroy = (scope) => {
    const elements = [];

    if (scope.matches?.(SELECTOR)) {
      elements.push(scope);
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => elements.push(element));

    elements.forEach((element) => {
      instances.get(element)?.destroy();
      instances.delete(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize()
    );
  } else {
    initialize();
  }

  /*
   * Compatibilidade com recarregamento de seções
   * dentro do editor de temas da Shopify.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => initialize(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => destroy(event.target)
  );
})();